import { Avatar, Flex, Group, Radio, Space, Text, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconInfoCircle } from "@tabler/icons-react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { ArrayProperties, Campo, TRespostaField } from "../../types";
import { isCampo } from "../../util";

type Properties = {
  item: ArrayProperties;
  disabled: boolean;
  tipo: TTiposDeRespostas;
  onChange: (value: TRespostaField, item: ArrayProperties) => void;
  horizontal: boolean;
  licenciado: TLicenciado | null | undefined;
};

const DISABLED_OPACITY = 0.6;

// eslint-disable-next-line @typescript-eslint/no-unsafe-call
const RadioComponent = ({ item, disabled, tipo, onChange, horizontal, licenciado }: Properties) => {
  const isSmMarches = useMediaQuery("(max-width: 48em)");
  const containerReference = useRef<HTMLDivElement>(null);
  const [, setSpaceInfo] = useState({ width: 0, height: 0 });
  const handleChangeRadio = useCallback(
    (value: Campo) => {
      if (!disabled) {
        onChange(value, item);
      }
    },
    [disabled, onChange, item]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>, value: Campo) => {
      if (event.key === " " || event.key === "Enter") {
        event.preventDefault();
        handleChangeRadio(value);
      }
    },
    [handleChangeRadio]
  );

  const handleFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    event.currentTarget.style.boxShadow = "0 0 0 2px #228be6";
  }, []);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    event.currentTarget.style.boxShadow = "none";
  }, []);

  const handleClick = useCallback(
    (value: Campo) => {
      return () => handleChangeRadio(value);
    },
    [handleChangeRadio]
  );

  const handleKeyDownWithValue = useCallback(
    (value: Campo) => {
      return (event: React.KeyboardEvent<HTMLDivElement>) => handleKeyDown(event, value);
    },
    [handleKeyDown]
  );

  const selectedField = isCampo(item.resposta) ? item.resposta.campo : null;
  useLayoutEffect(() => {
    if (containerReference.current) {
      const { clientWidth, clientHeight } = containerReference.current;
      setSpaceInfo({ width: clientWidth, height: clientHeight });
    }
  }, []);
  return (
    <Flex justify={"start"} align="center" direction="column" w={"100%"} style={{ overflow: "auto" }}>
      {item.obrigatoria ? (
        <>
          <Tooltip label="Está pergunta é obrigatória">
            <IconInfoCircle style={{ cursor: "pointer" }} />
          </Tooltip>
          <Space h="md" />
        </>
      ) : (
        <></>
      )}
      {licenciado && licenciado.logo && licenciado?.logo?.length > 0 ? (
        <>
          <Group>
            {licenciado.logo.map((item) => (
              <>
                <Avatar src={item?.path} radius="sm" size="lg" variant="default" />
                <Space h="md" />
              </>
            ))}
          </Group>
          <Space h="md" />
        </>
      ) : (
        <></>
      )}
      <Text fw={700} size="lg" w={isSmMarches ? "100%" : "50%"} ta="center">
        {item?.nome}
      </Text>
      <Space h="md" />
      <Text size="md" w={isSmMarches ? "100%" : "50%"} ta="center">
        {item?.descricao}
      </Text>
      <Space h="md" />
      <Flex
        ref={containerReference}
        align="stretch"
        justify="flex-start"
        gap="xs"
        direction={horizontal ? "row" : "column"}
        pr={10}
        style={{
          height: "fit-content",
          overflow: "visible"
        }}
        w={isSmMarches ? "100%" : "50%"}
        maw={isSmMarches ? "100%" : "50%"}
      >
        {tipo &&
          tipo.campos &&
          Array.isArray(tipo.campos) &&
          tipo.campos.map((value: Campo) => {
            const isSelected = selectedField === value.campo;
            return (
              <div
                key={value.valor}
                role="button"
                tabIndex={disabled ? -1 : 0} // Foco só se não estiver desabilitado
                onClick={handleClick(value)}
                onKeyDown={handleKeyDownWithValue(value)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  backgroundColor: isSelected ? "#f1f7ff" : "#f9f9f9",
                  border: isSelected ? "2px solid #228be6" : "1px solid #ccc",
                  borderRadius: "12px",
                  padding: "12px 16px",
                  transition: "all 0.2s ease",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? DISABLED_OPACITY : 1,
                  width: horizontal ? "auto" : "100%",
                  outline: "none" // importante para sobrescrever outline nativo
                }}
                aria-pressed={isSelected}
                // Adicione esta prop:
                onFocus={handleFocus}
                onBlur={handleBlur}
              >
                <Radio
                  readOnly
                  tabIndex={-1}
                  disabled={disabled}
                  checked={isSelected}
                  size="md"
                  radius="md"
                  styles={{
                    radio: {
                      marginRight: "12px",
                      flexShrink: 0,
                      cursor: "pointer"
                    }
                  }}
                />
                <Text
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: disabled ? "#999" : "#333",
                    cursor: "pointer",
                    flex: 1,
                    marginLeft: "5px"
                  }}
                >
                  {value.campo}
                </Text>
              </div>
            );
          })}
      </Flex>
      <Space h="md" />
    </Flex>
  );
};

export default RadioComponent;
